import React, { useState } from 'react';
import { X } from 'lucide-react';

const RegistrationForm = ({ isOpen, onClose, selectedProgram, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    program: selectedProgram?.title || '',
    paymentProof: null,
    paymentProofName: ''
  });
  const [showSuccess, setShowSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        paymentProof: file,
        paymentProofName: file.name
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Create FormData for file upload
    const formDataToSend = new FormData();
    formDataToSend.append('name', formData.name);
    formDataToSend.append('email', formData.email);
    formDataToSend.append('phone', formData.phone);
    formDataToSend.append('program', JSON.stringify(selectedProgram));
    if (formData.paymentProof) {
      formDataToSend.append('paymentProof', formData.paymentProof);
    }

    const registration = {
      id: Date.now(),
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      program: selectedProgram,
      paymentProof: formData.paymentProof,
      paymentProofName: formData.paymentProofName,
      status: 'pending',
      submittedAt: new Date().toISOString(),
      formData: formDataToSend
    };

    onSubmit(registration);
    
    // Show success popup
    setShowSuccess(true);
    
    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      program: '',
      paymentProof: null,
      paymentProofName: ''
    });

    // Close popup after 3 seconds
    setTimeout(() => {
      setShowSuccess(false);
      onClose();
    }, 3000);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">Pendaftaran Kelas</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {selectedProgram && (
            <div className="mb-6 p-4 bg-teal-50 rounded-lg">
              <h3 className="font-semibold text-teal-900 mb-1">{selectedProgram.title}</h3>
              <p className="text-sm text-teal-700">{selectedProgram.module}</p>
              <div className="mt-2 flex items-center justify-between">
                <span className="text-sm text-teal-600">{selectedProgram.date}</span>
                <span className="text-lg font-bold text-teal-600">{selectedProgram.price}</span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nama Lengkap <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="Masukkan nama lengkap"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="nama@email.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nomor WhatsApp <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="08xxxxxxxxxx"
                />
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold text-gray-900 mb-2">Informasi Pembayaran</h4>
                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <p className="text-sm text-gray-700 mb-2">Transfer ke rekening:</p>
                  <p className="font-mono text-sm mb-1">BRI: 2106-01-000176-30-8</p>
                  <p className="text-xs text-gray-600 mt-2">a.n. PT Velo Citra Teknologi</p>
                </div>

                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Upload Bukti Pembayaran <span className="text-gray-500 text-xs">(Opsional)</span>
                </label>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleFileChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
                {formData.paymentProofName && (
                  <p className="text-sm text-teal-600 mt-1">📎 {formData.paymentProofName}</p>
                )}
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
              >
                Batal
              </button>
              <button
                type="submit"
                className="flex-1 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition"
              >
                Kirim Pendaftaran
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    {/* Success Popup */}
    {showSuccess && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md mx-4 text-center animate-fadeIn">
          <div className="text-5xl mb-4">✓</div>
          <h3 className="text-2xl font-bold text-teal-600 mb-3">Pendaftaran Berhasil!</h3>
          <p className="text-gray-700 mb-4">
            Admin akan segera mengkonfirmasi pesananmu melalui WhatsApp. Pastikan nomormu aktif, ya!
          </p>
          <div className="text-sm text-gray-500">
            Pop-up ini akan ditutup secara otomatis...
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default RegistrationForm;
